import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, API } from '@/App';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  MapPin, Loader2, Search, Home, DollarSign, TrendingUp, 
  Building2, Calendar, ArrowLeft, BarChart3, Users, Map,
  Clock, ArrowUpRight, ArrowDownRight, Minus, FileText,
  Sparkles, Eye, Target, Percent, Crown, Lock, AlertCircle,
  ExternalLink
} from 'lucide-react';
import { ChatToggleButton } from "@/components/AIChatWidget";
import axios from 'axios';
import { toast } from 'sonner';
import { publicSitePath } from '@/utils/publicSite';

export default function PropertyResearch() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [address, setAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    fetchUser();
  }, []);

  const fetchUser = async () => {
    try {
      const response = await axios.get(`${API}/users/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data);
    } catch (error) {
      console.error('Failed to fetch user:', error);
    }
  };

  const isPro = user?.tier === 'pro' || user?.tier === 'developer';

  // Detect if address is Canadian
  const detectCountry = (addr) => {
    const addrUpper = addr.toUpperCase();
    // Canadian postal code pattern
    const canadianPostal = /[A-Z]\d[A-Z]\s?\d[A-Z]\d/.test(addrUpper);
    if (canadianPostal) return 'CA';
    
    // Canadian provinces
    const canadianProvinces = [' ON,', ' ON ', ' BC,', ' BC ', ' AB,', ' AB ', ' QC,', ' QC ', 
                               ' MB,', ' MB ', ' SK,', ' SK ', ' NS,', ' NS ', ' NB,', ' NB ',
                               'ONTARIO', 'BRITISH COLUMBIA', 'ALBERTA', 'QUEBEC', 'MANITOBA',
                               'TORONTO', 'VANCOUVER', 'CALGARY', 'OTTAWA', 'MONTREAL', 'CANADA'];
    
    for (const province of canadianProvinces) {
      if (addrUpper.includes(province)) return 'CA';
    }
    
    return 'US';
  };

  const handleSearch = async () => {
    if (!isPro) {
      toast.error('Property Research is a Pro feature. Please upgrade to access.');
      return;
    }

    if (!address.trim()) {
      toast.error('Please enter a property address');
      return;
    }

    setLoading(true);
    try {
      const country = detectCountry(address);
      let response;
      
      if (country === 'CA') {
        // Use Realtor.ca API for Canadian properties (more accurate)
        response = await axios.post(`${API}/market-data/canada/realtor-lookup`, {
          address: address
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Canadian property data loaded from Realtor.ca!');
      } else {
        // Use US API (Rentcast)
        response = await axios.post(`${API}/market-data/lookup`, {
          address: address,
          bedrooms: 3,
          bathrooms: 2
        }, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Property data loaded!');
      }

      setData(response.data);
    } catch (error) {
      console.error('Property research failed:', error);
      toast.error('Could not find data for this address');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value, country = 'US') => {
    if (!value) return 'N/A';
    const currency = country === 'CA' ? 'CAD' : 'USD';
    const locale = country === 'CA' ? 'en-CA' : 'en-US';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency,
      maximumFractionDigits: 0
    }).format(value);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // Calculate price per sqft from comps
  const getAvgPricePerSqft = () => {
    if (!data?.comparables?.length) return null;
    const valid = data.comparables.filter(c => c.sale_price && c.square_feet);
    if (!valid.length) return null;
    const avg = valid.reduce((sum, c) => sum + (c.sale_price / c.square_feet), 0) / valid.length;
    return Math.round(avg);
  };

  // Estimate monthly gross yield
  const getGrossYield = () => {
    if (!data?.estimated_value || !data?.estimated_rent) return null;
    return ((data.estimated_rent * 12) / data.estimated_value * 100).toFixed(2);
  };

  return (
    <div className="min-h-screen bg-slate-900">
      {/* Header */}
      <header className="glass border-b border-slate-800 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/dashboard')} className="text-slate-400">
                <ArrowLeft className="h-4 w-4 mr-2" />
                Dashboard
              </Button>
              <div className="flex items-center gap-2">
                <Search className="h-6 w-6 text-amber-500" />
                <h1 className="text-lg font-semibold text-white font-['Manrope']">Property Research</h1>
                <span className="px-2 py-0.5 text-xs font-bold bg-amber-500/20 text-amber-500 rounded">PRO</span>
              </div>
            </div>
            {data && isPro && (
              <Button
                onClick={() => navigate('/rental-analysis')}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-semibold"
              >
                <BarChart3 className="h-4 w-4 mr-2" />
                Analyze This Property
              </Button>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Upgrade Prompt for Light Users */}
        {!isPro && user && (
          <div className="bg-gradient-to-r from-amber-500/20 to-amber-500/5 border border-amber-500/30 rounded-xl p-8 mb-8 text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-amber-500/20 mb-4">
              <Crown className="h-8 w-8 text-amber-500" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-2">Upgrade to Pro</h2>
            <p className="text-slate-400 mb-6 max-w-xl mx-auto">
              Property Research is a Pro feature. Get instant access to live market data, 
              comparable sales, rent estimates, and auto-fill functionality.
            </p>
            <div className="flex items-center justify-center gap-4">
              <Button
                onClick={() => window.location.href = publicSitePath('/pricing')}
                className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8"
              >
                <Crown className="h-4 w-4 mr-2" />
                Upgrade to Pro - $59/mo
              </Button>
            </div>
            <div className="mt-6 grid grid-cols-3 gap-4 max-w-lg mx-auto">
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-500">Live</div>
                <div className="text-xs text-slate-500">Market Data</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-500">Comps</div>
                <div className="text-xs text-slate-500">Nearby Sales</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-amber-500">Auto</div>
                <div className="text-xs text-slate-500">Fill Magic</div>
              </div>
            </div>
          </div>
        )}

        {/* Search Section - Only for Pro Users */}
        {isPro && (
          <div className="glass-card rounded-xl p-6 mb-8">
            <div className="flex items-center gap-2 mb-4">
              <Sparkles className="h-5 w-5 text-amber-500" />
              <h2 className="text-lg font-semibold text-white">Find Property Intelligence</h2>
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold bg-blue-500/20 text-blue-400 rounded ml-2">
                🇺🇸 US
              </span>
              <span className="flex items-center gap-1 px-2 py-0.5 text-xs font-bold bg-red-500/20 text-red-400 rounded">
                🇨🇦 Canada
              </span>
            </div>
            <p className="text-slate-400 text-sm mb-4">
              Enter any <span className="text-amber-400 font-medium">US or Canadian</span> property address to get instant market data, comparable sales, and investment metrics.
            </p>
            
            <div className="flex gap-3">
              <div className="flex-1 relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-slate-500" />
                <Input
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  placeholder="Enter address (e.g., 123 Main St, Austin, TX or 100 Queen St, Toronto, ON)"
                className="pl-11 py-6 text-lg bg-slate-900 border-slate-700 text-white"
                onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
              />
            </div>
            <Button
              onClick={handleSearch}
              disabled={loading || !address.trim()}
              className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-8 py-6 text-lg"
            >
              {loading ? (
                <>
                  <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <Search className="h-5 w-5 mr-2" />
                  Search
                </>
              )}
            </Button>
          </div>
        </div>
        )}

        {/* Results */}
        {data && isPro && (
          <div className="space-y-6 animate-fade-in">
            {/* Property Overview */}
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-start justify-between mb-6">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1">{data.address}</h2>
                  <div className="flex items-center gap-4 text-sm text-slate-400">
                    {data.property_type && <span>{data.property_type}</span>}
                    {data.bedrooms && <span>{data.bedrooms} bed</span>}
                    {data.bathrooms && <span>{data.bathrooms} bath</span>}
                    {data.square_feet && <span>{data.square_feet.toLocaleString()} sqft</span>}
                    {data.year_built && <span>Built {data.year_built}</span>}
                  </div>
                </div>
                {data.cached && (
                  <span className="text-xs bg-slate-800 text-slate-400 px-2 py-1 rounded">
                    Cached Data
                  </span>
                )}
              </div>

              {/* Key Metrics Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {/* Estimated Value - show when available, otherwise show area info */}
                {data.estimated_value ? (
                  <div className="p-4 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-xl border border-emerald-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-5 w-5 text-emerald-500" />
                      <span className="text-sm text-slate-400">Estimated Value</span>
                    </div>
                    <div className="text-2xl font-bold text-emerald-500">
                      {formatCurrency(data.estimated_value, data.country)}
                    </div>
                    {data.value_low && data.value_high && (
                      <div className="text-xs text-slate-500 mt-1">
                        Range: {formatCurrency(data.value_low, data.country)} - {formatCurrency(data.value_high, data.country)}
                      </div>
                    )}
                    {data.country === 'CA' && !data.matched_property && (
                      <div className="text-xs text-slate-500 mt-1">
                        Based on nearby residential listings
                      </div>
                    )}
                  </div>
                ) : data.country === 'CA' ? (
                  <div className="p-4 bg-gradient-to-br from-slate-500/10 to-slate-500/5 rounded-xl border border-slate-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-5 w-5 text-slate-400" />
                      <span className="text-sm text-slate-400">Estimated Value</span>
                    </div>
                    <div className="text-xl font-bold text-slate-400">
                      N/A
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Price data unavailable
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 rounded-xl border border-emerald-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <DollarSign className="h-5 w-5 text-emerald-500" />
                      <span className="text-sm text-slate-400">Estimated Value</span>
                    </div>
                    <div className="text-2xl font-bold text-emerald-500">
                      {formatCurrency(data.estimated_value, data.country)}
                    </div>
                    {data.value_low && data.value_high && (
                      <div className="text-xs text-slate-500 mt-1">
                        Range: {formatCurrency(data.value_low, data.country)} - {formatCurrency(data.value_high, data.country)}
                      </div>
                    )}
                  </div>
                )}

                {/* Market Rent (US only) or City (Canada) */}
                {data.country === 'CA' ? (
                  <div className="p-4 bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 rounded-xl border border-cyan-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <MapPin className="h-5 w-5 text-cyan-500" />
                      <span className="text-sm text-slate-400">City</span>
                    </div>
                    <div className="text-2xl font-bold text-cyan-500">
                      {data.city || 'N/A'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      {data.province ? `${data.province}, ` : ''}🇨🇦 Canada
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gradient-to-br from-cyan-500/10 to-cyan-500/5 rounded-xl border border-cyan-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Home className="h-5 w-5 text-cyan-500" />
                      <span className="text-sm text-slate-400">Market Rent</span>
                    </div>
                    <div className="text-2xl font-bold text-cyan-500">
                      {formatCurrency(data.estimated_rent, data.country)}<span className="text-sm font-normal">/mo</span>
                    </div>
                    {data.rent_low && data.rent_high && (
                      <div className="text-xs text-slate-500 mt-1">
                        Range: {formatCurrency(data.rent_low, data.country)} - {formatCurrency(data.rent_high, data.country)}
                      </div>
                    )}
                  </div>
                )}

                {/* Gross Yield (US) or Listings Count (Canada) */}
                {data.country === 'CA' ? (
                  <div className="p-4 bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-xl border border-amber-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-5 w-5 text-amber-500" />
                      <span className="text-sm text-slate-400">Active Listings</span>
                    </div>
                    <div className="text-2xl font-bold text-amber-500">
                      {data.listings_for_sale?.length || 0}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Properties for sale
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gradient-to-br from-amber-500/10 to-amber-500/5 rounded-xl border border-amber-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Percent className="h-5 w-5 text-amber-500" />
                      <span className="text-sm text-slate-400">Gross Yield</span>
                    </div>
                    <div className="text-2xl font-bold text-amber-500">
                      {getGrossYield() || 'N/A'}%
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Annual rent / value
                    </div>
                  </div>
                )}

                {/* Cap Rate (US) or Recent Sales (Canada) */}
                {data.country === 'CA' ? (
                  <div className="p-4 bg-gradient-to-br from-violet-500/10 to-violet-500/5 rounded-xl border border-violet-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <TrendingUp className="h-5 w-5 text-violet-500" />
                      <span className="text-sm text-slate-400">Recent Sales</span>
                    </div>
                    <div className="text-2xl font-bold text-violet-500">
                      {data.recent_sales?.length || 0}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Sold in last 3 months
                    </div>
                  </div>
                ) : (
                  <div className="p-4 bg-gradient-to-br from-violet-500/10 to-violet-500/5 rounded-xl border border-violet-500/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Target className="h-5 w-5 text-violet-500" />
                      <span className="text-sm text-slate-400">Area Cap Rate</span>
                    </div>
                    <div className="text-2xl font-bold text-violet-500">
                      {data.neighborhood_cap_rate ? `${data.neighborhood_cap_rate}%` : 'N/A'}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                      Estimated neighborhood
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Two Column Layout */}
            <div className="grid lg:grid-cols-2 gap-6">
              {/* Comparable Sales / Active Listings */}
              <div className="glass-card rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                    {data.country === 'CA' ? (
                      <>
                        <Building2 className="h-5 w-5 text-emerald-500" />
                        Active Listings
                      </>
                    ) : (
                      <>
                        <TrendingUp className="h-5 w-5 text-emerald-500" />
                        Comparable Sales
                      </>
                    )}
                  </h3>
                  {data.country !== 'CA' && getAvgPricePerSqft() && (
                    <span className="text-sm text-slate-400">
                      Avg: <span className="text-emerald-500 font-semibold">${getAvgPricePerSqft()}/sqft</span>
                    </span>
                  )}
                </div>

                {/* US Comparables */}
                {data.country !== 'CA' && data.comparables && data.comparables.length > 0 ? (
                  <div className="space-y-3">
                    {data.comparables.map((comp, i) => (
                      <div key={i} className="p-4 bg-slate-800/50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="text-white font-medium truncate pr-4">
                              {comp.address || 'Nearby Property'}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                              {comp.bedrooms && <span>{comp.bedrooms} bed</span>}
                              {comp.bathrooms && <span>{comp.bathrooms} bath</span>}
                              {comp.square_feet && <span>{comp.square_feet.toLocaleString()} sqft</span>}
                              {comp.distance_miles && <span>{comp.distance_miles.toFixed(1)} mi away</span>}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-emerald-500 font-bold">
                              {formatCurrency(comp.sale_price, data.country)}
                            </div>
                            {comp.square_feet && comp.sale_price && (
                              <div className="text-xs text-slate-500">
                                ${Math.round(comp.sale_price / comp.square_feet)}/sqft
                              </div>
                            )}
                          </div>
                        </div>
                        {comp.sale_date && (
                          <div className="text-xs text-slate-500 flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            Sold {formatDate(comp.sale_date)}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                ) : data.country !== 'CA' ? (
                  <div className="text-center py-8 text-slate-500">
                    <TrendingUp className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No comparable sales data available</p>
                  </div>
                ) : null}

                {/* Canadian Active Listings */}
                {data.country === 'CA' && data.listings_for_sale && data.listings_for_sale.length > 0 ? (
                  <div className="space-y-3">
                    {data.listings_for_sale.slice(0, 5).map((listing, i) => (
                      <div key={i} className="p-4 bg-slate-800/50 rounded-lg">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <div className="text-white font-medium truncate pr-4">
                              {listing.address}, {listing.city}
                            </div>
                            <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                              {listing.bedrooms && <span>{listing.bedrooms} bed</span>}
                              {listing.bathrooms && <span>{listing.bathrooms} bath</span>}
                              {listing.lot_size && <span>{listing.lot_size.toLocaleString()} sqft lot</span>}
                              {listing.days_on_market && <span>{listing.days_on_market} days on market</span>}
                            </div>
                          </div>
                          <div className="text-right">
                            <div className="text-emerald-500 font-bold">
                              {formatCurrency(listing.price, 'CA')}
                            </div>
                            <div className="text-xs text-slate-500">
                              {listing.mls_id}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : data.country === 'CA' ? (
                  <div className="text-center py-8 text-slate-500">
                    <Building2 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No active listings found</p>
                  </div>
                ) : null}
              </div>

              {/* Rental Analysis (US) or Recent Sales (Canada) */}
              <div className="glass-card rounded-xl p-6">
                {data.country === 'CA' ? (
                  <>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                      <TrendingUp className="h-5 w-5 text-cyan-500" />
                      Recent Sales (Comps)
                    </h3>
                    {data.recent_sales && data.recent_sales.length > 0 ? (
                      <div className="space-y-3">
                        {data.recent_sales.slice(0, 5).map((sale, i) => (
                          <div key={i} className="p-4 bg-slate-800/50 rounded-lg">
                            <div className="flex justify-between items-start mb-2">
                              <div className="flex-1">
                                <div className="text-white font-medium truncate pr-4">
                                  {sale.address}, {sale.city}
                                </div>
                                <div className="flex items-center gap-3 text-xs text-slate-400 mt-1">
                                  {sale.bedrooms && <span>{sale.bedrooms} bed</span>}
                                  {sale.bathrooms && <span>{sale.bathrooms} bath</span>}
                                  {sale.lot_size && <span>{sale.lot_size.toLocaleString()} sqft lot</span>}
                                </div>
                              </div>
                              <div className="text-right">
                                <div className="text-cyan-500 font-bold">
                                  {formatCurrency(sale.sale_price, 'CA')}
                                </div>
                                {sale.sale_date && (
                                  <div className="text-xs text-slate-500">
                                    Sold {formatDate(sale.sale_date)}
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-slate-500">
                        <TrendingUp className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No recent sales data available</p>
                      </div>
                    )}
                  </>
                ) : (
                  <>
                    <h3 className="text-lg font-semibold text-white flex items-center gap-2 mb-4">
                      <Home className="h-5 w-5 text-cyan-500" />
                      Rental Market Analysis
                    </h3>

                <div className="space-y-4">
                  {/* Rent Estimate Visualization */}
                  <div className="p-4 bg-slate-800/50 rounded-lg">
                    <div className="flex justify-between items-center mb-3">
                      <span className="text-slate-400">Estimated Monthly Rent</span>
                      <span className="text-cyan-500 font-bold text-xl">
                        {formatCurrency(data.estimated_rent)}
                      </span>
                    </div>
                    
                    {/* Rent Range Bar */}
                    {data.rent_low && data.rent_high && (
                      <div className="relative">
                        <div className="h-2 bg-slate-700 rounded-full overflow-hidden">
                          <div 
                            className="h-full bg-gradient-to-r from-cyan-600 to-cyan-400 rounded-full"
                            style={{ 
                              width: `${((data.estimated_rent - data.rent_low) / (data.rent_high - data.rent_low)) * 100}%`,
                              marginLeft: '0%'
                            }}
                          ></div>
                        </div>
                        <div className="flex justify-between mt-2 text-xs text-slate-500">
                          <span>{formatCurrency(data.rent_low)}</span>
                          <span>{formatCurrency(data.rent_high)}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Quick Investment Metrics */}
                  <div className="grid grid-cols-2 gap-3">
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="text-xs text-slate-400 mb-1">Annual Rent</div>
                      <div className="text-lg font-semibold text-white">
                        {formatCurrency(data.estimated_rent ? data.estimated_rent * 12 : null)}
                      </div>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="text-xs text-slate-400 mb-1">Price/Rent Ratio</div>
                      <div className="text-lg font-semibold text-white">
                        {data.estimated_value && data.estimated_rent 
                          ? Math.round(data.estimated_value / data.estimated_rent) 
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="text-xs text-slate-400 mb-1">1% Rule</div>
                      <div className={`text-lg font-semibold ${
                        data.estimated_rent && data.estimated_value && 
                        (data.estimated_rent / data.estimated_value * 100) >= 1 
                          ? 'text-emerald-500' 
                          : 'text-amber-500'
                      }`}>
                        {data.estimated_rent && data.estimated_value 
                          ? `${(data.estimated_rent / data.estimated_value * 100).toFixed(2)}%`
                          : 'N/A'}
                      </div>
                    </div>
                    <div className="p-3 bg-slate-800/50 rounded-lg">
                      <div className="text-xs text-slate-400 mb-1">Days on Market</div>
                      <div className="text-lg font-semibold text-white">
                        {data.days_on_market || 'N/A'} days
                      </div>
                    </div>
                  </div>

                  {/* Last Sale Info */}
                  {data.last_sale_price && (
                    <div className="p-4 bg-slate-800/50 rounded-lg">
                      <h4 className="text-sm font-semibold text-slate-300 mb-2">Last Sale</h4>
                      <div className="flex justify-between items-center">
                        <div>
                          <div className="text-white font-bold text-lg">
                            {formatCurrency(data.last_sale_price)}
                          </div>
                          {data.last_sale_date && (
                            <div className="text-xs text-slate-500">
                              {formatDate(data.last_sale_date)}
                            </div>
                          )}
                        </div>
                        {data.estimated_value && data.last_sale_price && (
                          <div className={`flex items-center gap-1 px-3 py-1 rounded-full text-sm font-semibold ${
                            data.estimated_value > data.last_sale_price 
                              ? 'bg-emerald-500/20 text-emerald-500' 
                              : 'bg-red-500/20 text-red-500'
                          }`}>
                            {data.estimated_value > data.last_sale_price ? (
                              <ArrowUpRight className="h-4 w-4" />
                            ) : (
                              <ArrowDownRight className="h-4 w-4" />
                            )}
                            {Math.abs(((data.estimated_value - data.last_sale_price) / data.last_sale_price) * 100).toFixed(1)}%
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                  </>
                )}
              </div>
            </div>

            {/* Property Details */}
            <div className="glass-card rounded-xl p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-white flex items-center gap-2">
                  <Building2 className="h-5 w-5 text-slate-400" />
                  Property Details
                </h3>
                {data.matched_property === true && data.country === 'CA' && (
                  <span className="text-xs text-emerald-500 bg-emerald-500/10 px-2 py-1 rounded">
                    ✓ Property matched
                  </span>
                )}
                {data.matched_property === false && data.country === 'CA' && data.sample_property && (
                  <span className="text-xs text-amber-500 bg-amber-500/10 px-2 py-1 rounded">
                    Based on nearby residential listing
                  </span>
                )}
              </div>
              
              {/* API Note - show if there's a data limitation */}
              {data.api_note && (
                <div className="mb-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg text-sm text-blue-400">
                  <AlertCircle className="h-4 w-4 inline mr-2" />
                  {data.api_note}
                </div>
              )}
              
              {data.value_note && (
                <div className="mb-4 p-3 bg-amber-500/10 border border-amber-500/20 rounded-lg text-sm text-amber-400">
                  <AlertCircle className="h-4 w-4 inline mr-2" />
                  {data.value_note}
                </div>
              )}

              {/* For Canadian properties with exact match, show detailed property info */}
              {data.country === 'CA' && data.matched_property ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <DetailItem label="Property Type" value={data.property_type} />
                    <DetailItem label="Square Feet" value={data.square_feet?.toLocaleString()} />
                    <DetailItem label="Bedrooms" value={data.bedrooms} />
                    <DetailItem label="Bathrooms" value={data.bathrooms} />
                    <DetailItem label="MLS #" value={data.mls_id} />
                    <DetailItem label="Data Source" value={data.data_source?.toUpperCase()} />
                  </div>
                  {data.description && (
                    <div className="mt-4 p-4 bg-slate-800/50 rounded-lg">
                      <h4 className="text-sm font-semibold text-slate-300 mb-2">Property Description</h4>
                      <p className="text-sm text-slate-400">{data.description}</p>
                    </div>
                  )}
                </>
              ) : data.country === 'CA' && data.sample_property ? (
                /* For Canadian properties with sample residential property */
                <>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <DetailItem label="Property Type" value={data.sample_property.property_type} />
                    <DetailItem label="Square Feet" value={data.sample_property.square_feet} />
                    <DetailItem label="Bedrooms" value={data.sample_property.bedrooms} />
                    <DetailItem label="Bathrooms" value={data.sample_property.bathrooms} />
                    <DetailItem label="MLS #" value={data.sample_property.mls_id} />
                    <DetailItem label="Data Source" value={data.data_source?.toUpperCase()} />
                  </div>
                  {data.sample_property.description && (
                    <div className="mt-4 p-4 bg-slate-800/50 rounded-lg">
                      <h4 className="text-sm font-semibold text-slate-300 mb-2">Sample Residential Listing</h4>
                      <p className="text-sm text-slate-400">{data.sample_property.description}</p>
                    </div>
                  )}
                  <div className="mt-4 p-3 bg-slate-700/30 rounded-lg text-xs text-slate-400">
                    <strong>Note:</strong> The exact property was not found in active listings. 
                    Details shown are from a nearby residential property for reference.
                    For specific property details, visit{' '}
                    <a href="https://www.realtor.ca" target="_blank" rel="noopener noreferrer" className="text-cyan-500 hover:text-cyan-400">
                      Realtor.ca
                    </a>
                  </div>
                </>
              ) : data.country === 'CA' ? (
                /* For Canadian properties without any sample data */
                <div className="text-center py-6">
                  <MapPin className="h-12 w-12 text-slate-600 mx-auto mb-3" />
                  <p className="text-slate-400 mb-2">
                    No residential property details available for this search.
                  </p>
                  <p className="text-sm text-slate-500 max-w-md mx-auto">
                    Browse the <strong className="text-amber-500">{data.listings_for_sale?.length || 0} active listings</strong> above to explore the market in this area.
                  </p>
                </div>
              ) : (
                /* US properties - show standard details grid */
                <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                  <DetailItem label="Bedrooms" value={data.bedrooms} />
                  <DetailItem label="Bathrooms" value={data.bathrooms} />
                  <DetailItem label="Square Feet" value={data.square_feet?.toLocaleString()} />
                  <DetailItem label="Lot Size" value={data.lot_size ? `${data.lot_size.toLocaleString()} sqft` : null} />
                  <DetailItem label="Year Built" value={data.year_built} />
                  <DetailItem label="Property Type" value={data.property_type} />
                  {data.mls_id && <DetailItem label="MLS #" value={data.mls_id} />}
                  {data.days_on_market !== undefined && data.days_on_market !== null && <DetailItem label="Days Listed" value={data.days_on_market} />}
                  <DetailItem label="County" value={data.county} />
                  <DetailItem label="Data Source" value={data.data_source?.toUpperCase()} />
                </div>
              )}
              
              {data.listing_url && (
                <div className="mt-4">
                  <a 
                    href={data.listing_url} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-cyan-500 hover:text-cyan-400 flex items-center gap-1"
                  >
                    View full listing on source website
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
              )}
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-amber-500/20 to-amber-500/5 border border-amber-500/30 rounded-xl p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-xl font-bold text-white mb-2">Ready to Analyze This Property?</h3>
                  <p className="text-slate-400">
                    Run a full investment analysis with cash flow projections, stress testing, and deal scoring.
                  </p>
                </div>
                <Button
                  onClick={() => navigate('/rental-analysis')}
                  className="bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold px-6 py-3"
                >
                  <BarChart3 className="h-5 w-5 mr-2" />
                  Full Analysis
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* Empty State - Only for Pro Users */}
        {!data && !loading && isPro && (
          <div className="text-center py-16">
            <Search className="h-16 w-16 text-slate-700 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">Search for a Property</h3>
            <p className="text-slate-400 max-w-md mx-auto">
              Enter any US property address above to get instant market intelligence including 
              comparable sales, rental estimates, and investment metrics.
            </p>
          </div>
        )}
      </main>
    </div>
  );
}

// Helper component for property details
function DetailItem({ label, value }) {
  return (
    <div className="p-3 bg-slate-800/30 rounded-lg">
      <div className="text-xs text-slate-500 mb-1">{label}</div>
      <div className="text-white font-medium">{value || 'N/A'}</div>
    </div>
  );
}
